import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { TreeView, type TreeDataItem } from '@/shared/components/ui/TreeView';
import { AnyFieldApi } from '@tanstack/react-form';
import { useCompanyHierarchy } from '@/shared/hooks/useIssues';
import { FieldInfo } from '@/shared/components/form/FieldInfo';
import { IssueType, type IssueWithChildren } from '@momentum/types';
import { X, ChevronDown, Lock } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { isRestrictedIssue } from '@/shared/utils/issue-guards';

interface ParentGoalSelectorProps {
  disabled?: boolean;
  field: AnyFieldApi;
  types?: IssueType[];
}

export const ParentGoalSelector: React.FC<ParentGoalSelectorProps> = ({
  disabled = false,
  field,
}) => {
  const { t } = useTranslation('goals');
  const [open, setOpen] = useState(false);

  const { data: goals = [], isLoading } = useCompanyHierarchy({
    issueTypes: [IssueType.GOAL, IssueType.TASK],
  });

  const treeData = useMemo((): TreeDataItem[] => {
    const convertIssueToTreeItem = (
      issue: IssueWithChildren
    ): TreeDataItem | null => {
      // Only include goals (exclude tasks)
      if (issue.type === IssueType.TASK) {
        return null;
      }

      const isRestricted = isRestrictedIssue(issue);

      const children =
        issue.children
          ?.map(convertIssueToTreeItem)
          .filter((item): item is TreeDataItem => item !== null) || [];

      return {
        id: issue._id,
        name: isRestricted ? t('restrictedGoal.title') : issue.title,
        icon: isRestricted ? Lock : undefined,
        disabled: isRestricted,
        children: children.length > 0 ? children : undefined,
      };
    };

    return goals
      .map(convertIssueToTreeItem)
      .filter((item): item is TreeDataItem => item !== null);
  }, [goals, t]);

  const selectedGoal = useMemo(() => {
    if (!field.state.value) return null;

    // Search for the goal (including restricted goals)
    const findGoalById = (
      items: IssueWithChildren[],
      id: string
    ): IssueWithChildren | null => {
      for (const item of items) {
        if (item._id === id) return item;

        if (item.children) {
          const found = findGoalById(item.children, id);

          if (found) return found;
        }
      }

      return null;
    };

    return findGoalById(goals, field.state.value);
  }, [goals, field.state.value]);

  const handleSelectChange = (item: TreeDataItem | undefined) => {
    if (!item || item.id === 'none' || item.disabled) {
      if (item?.disabled) return;

      field.handleChange(undefined);
    } else {
      field.handleChange(item.id);
    }
    setOpen(false);
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    field.handleChange(undefined);
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-900">
        {t('parentGoalLabel')}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id="parent"
            type="button"
            disabled={disabled || isLoading}
            onBlur={field.handleBlur}
            className={cn(
              'w-full flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors min-h-[56px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50',
              field.state.meta.errors.length > 0
                ? 'border-red-500'
                : 'border-gray-300',
              !selectedGoal && !field.state.value && 'text-gray-400'
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="text-sm text-gray-900 truncate flex-1 text-start flex items-center gap-2">
              {selectedGoal ? (
                isRestrictedIssue(selectedGoal) ? (
                  <>
                    <Lock className="h-4 w-4 text-gray-500 shrink-0" />
                    <span>{t('restrictedGoal.title')}</span>
                  </>
                ) : (
                  selectedGoal.title
                )
              ) : (
                t('selectParentGoal')
              )}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-gray-400 transition-transform shrink-0',
                'me-2 [dir="rtl"]:order-first [dir="rtl"]:rotate-180 [dir="rtl"]:ms-2 [dir="rtl"]:me-0',
                open && 'rotate-180'
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <div className="max-h-[400px] overflow-auto">
            <TreeView
              data={treeData}
              initialSelectedItemId={field.state.value || 'none'}
              onSelectChange={handleSelectChange}
              expandAll={false}
              className="p-2"
            />
          </div>
          {(selectedGoal || field.state.value) && (
            <div className="border-t p-2">
              <button
                type="button"
                className="w-full flex items-center justify-start text-xs text-gray-600 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                onClick={handleClearSelection}
              >
                <X className="me-2 h-4 w-4" />
                {t('noParentGoal')}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <FieldInfo field={field} />
    </div>
  );
};
